import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAppSelector } from 'hooks';
import { identifyPendo, resetPendoIdentity } from 'services/PendoService';
import { getAppVersions, loadAppVersions } from 'utils/version';

/**
 * Watches the Redux user slice and calls Pendo's identify() once the player is
 * hydrated (non-guest, has a stable playerGUID). Safe to mount once.
 *
 * The identity sent is the engine `playerGUID` (EN-10466), so one Pendo visitor
 * is one player row. `guest` is checked as well as `playerGUID` because the
 * slice keeps the previous member's GUID readable until clearAuth() runs; a
 * guest session must never re-identify as the player who last used the browser.
 */
export function usePendoIdentify(): void {
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const displayName = useAppSelector((state) => state.user.displayName);
  const guest = useAppSelector((state) => state.user.guest);
  const activePlayerSession = useRef<string | null>(null);

  useEffect(() => {
    if (guest || !playerGUID) {
      if (activePlayerSession.current) {
        resetPendoIdentity();
        activePlayerSession.current = null;
      }
      return;
    }
    if (activePlayerSession.current && activePlayerSession.current !== playerGUID) {
      resetPendoIdentity();
    }
    // Session cleanup must not depend on the analytics provider being healthy.
    // A failed identify can still have identity-bound events waiting locally.
    activePlayerSession.current = playerGUID;
    let active = true;
    // Await the native read so the visitor carries the shell version too;
    // it resolves immediately on web and from cache after the first call.
    loadAppVersions()
      .then(() => {
        if (!active) return;
        const { nativeVersion, nativeBuild, bundleVersion } = getAppVersions();
        identifyPendo({
          playerGUID,
          displayName,
          platform: Capacitor.getPlatform(), // 'ios' | 'android' | 'web'
          appVersion: nativeVersion,
          appBuild: nativeBuild,
          bundleVersion
        });
      })
      // Belt-and-braces: loadAppVersions swallows a failed native read and
      // resolves with the bundle-only shape, so this cannot currently reject.
      // Kept so a future change in there cannot become an unhandled rejection
      // on a hot path.
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [guest, playerGUID, displayName]);
}
